import { useEffect, useRef, useCallback } from 'react'

declare global {
  interface Window {
    gapi: any
    google: any
  }
}

const PICKER_API_KEY = process.env.REACT_APP_GOOGLE_PICKER_API_KEY ?? ''
const CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID ?? ''
const SCOPES =
  'https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/drive.file'

export type PickerFile = {
  id: string
  name: string
  mimeType: string
  thumbnailUrl: string
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.onload = () => resolve()
    script.onerror = reject
    document.body.appendChild(script)
  })
}

export function useGooglePicker() {
  const pickerReadyRef = useRef(false)

  useEffect(() => {
    Promise.all([
      loadScript('https://apis.google.com/js/api.js'),
      loadScript('https://accounts.google.com/gsi/client'),
    ]).then(() => {
      window.gapi.load('picker', () => {
        pickerReadyRef.current = true
      })
    })
  }, [])

  const openPicker = useCallback((onSelect: (files: PickerFile[], accessToken: string) => void) => {
    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPES,
      callback: (tokenResponse: any) => {
        if (tokenResponse.error) return

        const accessToken = tokenResponse.access_token

        const imageView = new window.google.picker.View(
          window.google.picker.ViewId.DOCS_IMAGES
        ).setMimeTypes('image/jpeg,image/png,image/gif,image/webp,image/tiff')

        const folderView = new window.google.picker.DocsView()
          .setMimeTypes('image/jpeg,image/png,image/gif,image/webp,image/tiff')
          .setSelectFolderEnabled(false)
          .setIncludeFolders(true)

        const sharedDriveView = new window.google.picker.DocsView()
          .setMimeTypes('image/jpeg,image/png,image/gif,image/webp,image/tiff')
          .setSelectFolderEnabled(false)
          .setIncludeFolders(true)
          .setEnableDrives(true)

        const picker = new window.google.picker.PickerBuilder()
          .addView(imageView)
          .addView(folderView)
          .addView(sharedDriveView)
          .setOAuthToken(accessToken)
          .setDeveloperKey(PICKER_API_KEY)
          .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
          .enableFeature(window.google.picker.Feature.SUPPORT_DRIVES)
          .setCallback((data: any) => {
            if (data[window.google.picker.Response.ACTION] === window.google.picker.Action.PICKED) {
              const files: PickerFile[] = data[window.google.picker.Response.DOCUMENTS].map(
                (doc: any) => ({
                  id: doc[window.google.picker.Document.ID],
                  name: doc[window.google.picker.Document.NAME],
                  mimeType: doc[window.google.picker.Document.MIME_TYPE],
                  thumbnailUrl: doc[window.google.picker.Document.THUMBNAILS]?.[0]?.url ?? '',
                })
              )
              onSelect(files, accessToken)
            }
          })
          .build()

        picker.setVisible(true)
      },
    })

    tokenClient.requestAccessToken({ prompt: '' })
  }, [])

  return { openPicker }
}
